import React, { ReactNode } from 'react';

import {
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { notNullish } from '../utils/utils';

/**
 * A row is normally one cell per column. `{ fullWidth }` instead renders a single cell
 * spanning every column — for detail panels expanded underneath a row.
 */
export type SimpleTableRow =
  | ReactNode[]
  | { fullWidth: ReactNode; sx?: SxProps }
  | null;

type Props = {
  rows: SimpleTableRow[];
  sx?: SxProps;
  elevation?: number;
  firstColBold?: boolean;
  lastColBold?: boolean;
  firstLineBold?: boolean;
  title?: ReactNode;
  tableSx?: SxProps;
  footer?: ReactNode;
  noPaper?: boolean;
};

export const SimpleTable = ({
  rows,
  elevation,
  firstColBold,
  lastColBold,
  firstLineBold,
  sx = {},
  title,
  tableSx = {},
  footer,
  noPaper,
}: Props) => {
  const visibleRows = rows.filter(notNullish);

  const columnCount = Math.max(
    1,
    ...visibleRows.map((row) => (Array.isArray(row) ? row.length : 1))
  );

  return (
    <TableContainer
      component={noPaper ? 'div' : Paper}
      elevation={elevation || 0}
      sx={{ py: 2, ...sx }}
    >
      {title && (
        <Typography variant="h3" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <Table size="small" sx={{ ...tableSx }}>
        <TableBody>
          {visibleRows.map((row, i) =>
            !Array.isArray(row) ? (
              <TableRow key={i}>
                <TableCell colSpan={columnCount} sx={{ ...row.sx }}>
                  {row.fullWidth}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow
                key={i}
                sx={
                  firstLineBold && i === 0
                    ? { '& td': { fontWeight: 700 } }
                    : {}
                }
              >
                {row.map((content, ii) => {
                  const sx =
                    ii === row.length - 1
                      ? {
                          textAlign: 'right',
                          fontWeight: lastColBold ? 700 : 400,
                        }
                      : ii === 0
                      ? { fontWeight: firstColBold ? 700 : 400 }
                      : {};

                  return (
                    <TableCell key={ii} sx={sx}>
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            )
          )}
        </TableBody>
      </Table>

      {footer}
    </TableContainer>
  );
};
